import type { CSSProperties, FC } from 'react';
import type { AuthorKPIs, AuthorOverlayKey, AuthorProfile } from '../types';
import {
  RequestFolderIcon,
  ClipboardCheckIcon,
  AcademicCapIcon,
  CalendarClockIcon,
  WrenchScrewdriverIcon,
} from './AuthorIcons';

interface Props {
  kpis: AuthorKPIs;
  profile: AuthorProfile;
  onOpenOverlay: (key: AuthorOverlayKey) => void;
}

const CARDS: Array<{
  key: AuthorOverlayKey;
  num: string;
  title: string;
  text: string;
  accent: string;
  icon: FC<{ className?: string }>;
  meta: (kpis: AuthorKPIs, completion: number) => string;
}> = [
  {
    key: 'MY_REQUESTS',
    num: '01',
    title: 'Мои заявки',
    text: 'Отправленные обращения, статусы, черновики и история взаимодействия.',
    meta: (k) => `Активных: ${k.in_progress ?? 0}`,
    accent: '#41d2ff',
    icon: RequestFolderIcon,
  },
  {
    key: 'SURVEYS_AND_FORMS',
    num: '02',
    title: 'Опросы и формы',
    text: 'Анкета автора, публикационные предпочтения и дополнительные формы.',
    accent: '#49ddb7',
    icon: ClipboardCheckIcon,
    meta: (_k, c) => `Профиль: ${c}%`,
  },
  {
    key: 'ACADEMIC_CONNECTIONS',
    num: '03',
    title: 'Академические связи',
    text: 'Личные переписки с соавторами, редактором и командой сопровождения.',
    accent: '#5da8ff',
    icon: AcademicCapIcon,
    meta: (k) => `Сообщений: ${k.unread_messages ?? 0}`,
  },
  {
    key: 'CALENDAR',
    num: '04',
    title: 'Служебный календарь',
    text: 'Сроки рукописей, ответов редакции, подач и согласований.',
    accent: '#9e80ff',
    icon: CalendarClockIcon,
    meta: (k) => `Дедлайнов: ${k.urgent_deadlines ?? 0}`,
  },
  {
    key: 'RESEARCH_TOOLS',
    num: '05',
    title: 'Программы и инструменты',
    text: 'Опросы, исследовательские эксперименты и анализ данных.',
    accent: '#e2ad3a',
    icon: WrenchScrewdriverIcon,
    meta: () => '36 инструментов',
  },
];

export const AuthorCabinetGrid: React.FC<Props> = ({ kpis, profile, onOpenOverlay }) => {
  const completion = profile.completion_percentage || 75;

  return (
    <div className="owner-hub-grid author-cabinet-grid">
      {CARDS.map((card) => {
        const Icon = card.icon;
        return (
          <button
            key={card.key}
            type="button"
            className="owner-hub-card"
            style={{ '--action': card.accent } as CSSProperties}
            onClick={() => onOpenOverlay(card.key)}
          >
            <span className="owner-hub-index">{card.num}</span>
            <span className="owner-hub-icon" aria-hidden="true">
              <Icon className="w-6 h-6" />
            </span>
            <span className="owner-hub-copy">
              <strong>{card.title}</strong>
              <span>
                {card.text} · {card.meta(kpis, completion)}
              </span>
            </span>
            <span className="owner-hub-arrow" aria-hidden="true">
              ›
            </span>
          </button>
        );
      })}
    </div>
  );
};
